import { ReactNode, useMemo, useState } from "react";
import {
  AdoptiveParentInformation,
  ProfileAnswer,
  ProfileMedia,
} from "../../types/coreApiMirror";
import { Handwriting, Paragraph } from "../text";
import { MediumAnswerTile, SmallAnswerTile } from "./answer";
import Carousel from "./carousel";
import { LargeMediaTile, SmallMediaTile } from "./media";
import ParentDetailsCard from "./parentDetailsCard";
import { VideoThumb } from "../VideoThumb";
import VideoThumbCoreAPI from "../VideoThumb/VideoThumbCoreAPI";
import GalleryTile from "./galleryTile";

export type FamProfileGalleryProps = {
  media: ProfileMedia[];
  answers: ProfileAnswer[];
  parents: AdoptiveParentInformation[];
  welcomeMessage?: string;
  homeStudyApproved: boolean;
  state?: string;
  video_url?: string;
  video_thumb?: ProfileMedia;
};

type Region = "A" | "B" | "C" | "D";
const pattern: Array<Region> = [
  "A",
  "B",
  "C",
  "C",
  "D",
  "B",
  "A",
  "C",
  "D",
  "C",
  "C",
  "D",
];

type AnswerCContent = {
  type: "C";
  content: ProfileAnswer;
};

type AnswerDContent = {
  type: "D";
  content1: ProfileAnswer;
  content2: ProfileAnswer;
};

type MediaAContent = {
  type: "A";
  content: ProfileMedia;
  index: number;
};

type MediaBContent = {
  type: "B";
  content1: ProfileMedia;
  content2: ProfileMedia;
  index1: number;
  index2: number;
};

type RegionContent =
  | AnswerCContent
  | AnswerDContent
  | MediaAContent
  | MediaBContent;

export function FamProfileGallery(props: FamProfileGalleryProps) {
  const { media, answers, parents, welcomeMessage, video_url, video_thumb } =
    props;
  const [activeMediaIndex, setActiveMediaIndex] = useState<number>(-1);

  // this insane looking routine has two parts:
  //
  // first:
  // create an array of gallery content (Answers/Media) in the order
  // specified by "pattern".
  //
  // second:
  // we loop through the curated gallery content and render the
  // appropriate gallery tiles. Why not combine this with the first step? The
  // media tiles have an onClick handler that consumes mediaIndex; since
  // this is updated periodically during the while loop, but the handlers are
  // not executed until some arbitrary future user input event, they will all
  // incorrectly be called with the final value of mediaIndex. We use the
  // intermediary regionContent array with map to circumvent this.
  const regions = useMemo(() => {
    let mediaArr = media.slice();
    let answersArr = answers.slice();
    let mediaIndex = 0;
    let answersIndex = 0;

    let nextRegion: Region = pattern[0];
    let iteration = 0;

    let regionContent: Array<RegionContent> = [];

    while (mediaIndex < mediaArr.length || answersIndex < answersArr.length) {
      switch (nextRegion) {
        case "A":
          if (mediaIndex >= mediaArr.length) {
            break;
          }
          regionContent.push({
            type: "A",
            content: mediaArr[mediaIndex],
            index: mediaIndex,
          });
          mediaIndex++;
          break;
        case "B":
          if (mediaIndex >= mediaArr.length) {
            break;
          }
          const numMedia = 2;
          let bMedia = mediaArr.slice(mediaIndex, mediaIndex + numMedia);
          if (bMedia.length === 1) {
            regionContent.push({
              type: "A",
              content: bMedia[0],
              index: mediaIndex,
            });
          } else {
            regionContent.push({
              type: "B",
              content1: bMedia[0],
              content2: bMedia[1],
              index1: mediaIndex,
              index2: mediaIndex + 1,
            });
          }
          mediaIndex += numMedia;
          break;
        case "C":
          if (answersIndex >= answersArr.length) {
            break;
          }
          regionContent.push({
            type: "C",
            content: answersArr[answersIndex],
          });
          answersIndex++;
          break;
        case "D":
          if (answersIndex >= answersArr.length) {
            break;
          }
          const numAnswers = 2;
          let dAnswers = answersArr.slice(
            answersIndex,
            answersIndex + numAnswers,
          );
          regionContent.push({
            type: "D",
            content1: dAnswers[0],
            content2: dAnswers[1],
          });
          answersIndex += numAnswers;
          break;
      }
      iteration++;
      nextRegion = pattern[iteration % pattern.length];
    }

    const regions = regionContent.map((content) => {
      // build region component
      switch (content.type) {
        case "A":
          return (
            <GalleryRegionA
              key={content.content.media_url}
              onMediaClick={() => {
                setActiveMediaIndex(content.index);
              }}
              media={content.content}
            />
          );
        case "B":
          return (
            <GalleryRegionB
              key={`${content.content1.media_url}---${content.content2.media_url}`}
              media1={content.content1}
              onMedia1Click={() => setActiveMediaIndex(content.index1)}
              onMedia2Click={() => setActiveMediaIndex(content.index2)}
              media2={content.content2}
            />
          );
        case "C":
          return (
            <GalleryRegionC
              key={`${content.content.question.name}---${content.content.adoptive_parent.uuid}`}
              answer={content.content}
            />
          );
        case "D":
          return (
            <GalleryRegionD
              key={`${content.content1.question.name}---${content.content1.adoptive_parent.uuid}`}
              answer1={content.content1}
              answer2={content.content2}
            />
          );
      }
    });

    return regions;
  }, [media, answers]);

  return (
    <>
      {parents.map((p, i) => {
        return (
          <ParentDetailsCard
            state={props.state}
            homeStudyApproved={props.homeStudyApproved}
            key={p.uuid}
            single={parents.length === 1}
            {...p}
          />
        );
      })}

      {welcomeMessage && (
        <GalleryTile className="bg-white p-10" size="lg">
          <div>
            {video_url && (
              <VideoThumbCoreAPI
                posterImage={video_thumb?.media_url ?? "#"}
                videoURL={video_url ?? "#"}
                classNames="float-right w-[220px] mb-6 ml-6"
              />
            )}
            <div className="text-deco-head-6 mb-3 text-onyx">Hello!</div>
            <Paragraph color="gray" size="sm" className="whitespace-pre-wrap">
              {welcomeMessage}
            </Paragraph>
            <Handwriting
              size="lg"
              color="teal"
              className="mt-3 before:mr-1 before:content-['-']"
            >
              {parents.map((p) => p.first_name).join(" & ")}
            </Handwriting>
          </div>
        </GalleryTile>
      )}

      {regions}
      {activeMediaIndex > -1 && (
        <Carousel
          disableNext={activeMediaIndex >= media.length - 1}
          disablePrev={activeMediaIndex <= 0}
          onNext={() => {
            setActiveMediaIndex(
              Math.min(activeMediaIndex + 1, media.length - 1),
            );
          }}
          onPrev={() => {
            setActiveMediaIndex(Math.max(activeMediaIndex - 1, 0));
          }}
          onClose={() => {
            setActiveMediaIndex(-1);
          }}
          mediaUrl={media[activeMediaIndex].media_url}
          mediaDescription={media[activeMediaIndex].description}
        />
      )}
    </>
  );
}

type GalleryRegionAProps = {
  media: ProfileMedia;
  onMediaClick: () => void;
};

function GalleryRegionA(props: GalleryRegionAProps) {
  return (
    <>
      {props.media && (
        <LargeMediaTile onMediaClick={props.onMediaClick} media={props.media} />
      )}
    </>
  );
}

type GalleryRegionBProps = {
  media1: ProfileMedia;
  onMedia1Click: () => void;
  media2: ProfileMedia;
  onMedia2Click: () => void;
};

function GalleryRegionB(props: GalleryRegionBProps) {
  return (
    <>
      {props.media1 && (
        <SmallMediaTile
          onMediaClick={props.onMedia1Click}
          media={props.media1}
        />
      )}
      {props.media2 && (
        <SmallMediaTile
          onMediaClick={props.onMedia2Click}
          media={props.media2}
        />
      )}
    </>
  );
}

type GalleryRegionCProps = {
  answer: ProfileAnswer;
};

function GalleryRegionC(props: GalleryRegionCProps) {
  return <>{props.answer && <MediumAnswerTile {...props.answer} />}</>;
}

type GalleryRegionDProps = {
  answer1: ProfileAnswer;
  answer2: ProfileAnswer;
};

function GalleryRegionD(props: GalleryRegionDProps) {
  return (
    <>
      {props.answer1 && <SmallAnswerTile {...props.answer1} />}
      {props.answer2 && <SmallAnswerTile {...props.answer2} />}
    </>
  );
}

export default FamProfileGallery;
